import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useSwipeable } from 'react-swipeable';
import { userAPI } from '../../../services/api';
import { useUserRedux } from '../../../hooks/useUserRedux';
import { startTutorial } from '../../tutorial/store/tutorialSlice';
import { useTranslation } from 'react-i18next';
import { tr } from '../../../i18n/msg';
import { onboardingSlides as slides, ONBOARDING_BUTTONS } from '../onboardingSlides';
import OnboardingIcon from './OnboardingIcon';
import '../../../styles/onboardingCarousel.css';

// Сколько секунд кнопка «Next» неактивна на каждом новом слайде — чтобы успели прочитать.
const READ_DELAY_MS = 3000;

function SlideMedia({ slide, active, onLoad, loaded }) {
  const { t: translate } = useTranslation();
  const t = (v) => tr(translate, v);
  return (
    <div className={`ob-frame ob-frame--${slide.imageMode || 'cover'} ${loaded ? 'is-loaded' : ''}`}>
      <img
        src={slide.image}
        alt={t(slide.title)}
        className="ob-image"
        style={slide.imagePosition ? { objectPosition: slide.imagePosition } : undefined}
        draggable={false}
        decoding="async"
        onLoad={onLoad}
        onError={onLoad}
      />

      {slide.tags && (
        <div className="ob-tags">
          {slide.tags.map((tag) => (
            <span key={t(tag.text)} className={`ob-chip ob-chip--${tag.tone || 'purple'}`}>{t(tag.text)}</span>
          ))}
        </div>
      )}

      {slide.pins?.map((pin) => (
        <span key={t(pin.text)} className={`ob-chip ob-chip--pin ob-chip--${pin.tone}`} style={pin.style}>
          {t(pin.text)}
        </span>
      ))}

      {slide.highlight && (
        <div className="ob-tags">
          <span className="ob-chip ob-chip--highlight">
            <b>{t(slide.highlight.label)}</b>
            {t(slide.highlight.value)}
          </span>
        </div>
      )}

      {slide.badge && (
        <div className={`ob-badge ${slide.badge.tone ? `ob-badge--${slide.badge.tone}` : ''} ${active ? 'is-visible' : ''}`}>
          <OnboardingIcon icon={slide.badge.icon} size={slide.badge.icon.startsWith('/') ? 24 : 18} />
          <span>{t(slide.badge.text)}</span>
        </div>
      )}

      {slide.lairBar && (
        <div className={`ob-lair ${active ? 'is-visible' : ''}`}>
          {slide.lairBar.icons.map((src) => (
            <img key={src} src={src} alt="" className="ob-lair-icon" draggable={false} />
          ))}
          <div className="ob-lair-text">
            <span className="ob-lair-title">{t(slide.lairBar.title)}</span>
            <span className="ob-lair-sub">
              <img src="/onboarding/icon-coin.jpg" alt="" />
              {t(slide.lairBar.sub)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function OnboardingCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // Индекс последнего слайда, на котором уже прошла пауза чтения; дальше readIndex + 1 листать нельзя.
  const [readIndex, setReadIndex] = useState(-1);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: user, setUser } = useUserRedux();
  const { t: translate } = useTranslation();
  const t = (v) => tr(translate, v);

  const isLast = currentIndex === slides.length - 1;
  const slide = slides[currentIndex];
  const canAdvance = readIndex >= currentIndex;
  const features = slide.features;

  useEffect(() => {
    if (readIndex >= currentIndex) return undefined;
    const timer = setTimeout(() => {
      setReadIndex((prev) => Math.max(prev, currentIndex));
    }, READ_DELAY_MS);
    return () => clearTimeout(timer);
  }, [currentIndex, readIndex]);

  // Прогреваем все картинки сразу, чтобы при листании они уже были в кэше и не «мигали».
  useEffect(() => {
    slides.forEach(({ image }) => {
      const img = new Image();
      img.decoding = 'async';
      img.src = image;
    });
  }, []);

  const markLoaded = (src) => {
    setLoadedImages((prev) => (prev[src] ? prev : { ...prev, [src]: true }));
  };

  const goTo = (index) => {
    if (index >= 0 && index < slides.length && index <= readIndex + 1) {
      setCurrentIndex(index);
    }
  };

  const nextSlide = () => goTo(currentIndex + 1);
  const prevSlide = () => goTo(currentIndex - 1);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: nextSlide,
    onSwipedRight: prevSlide,
    trackMouse: true,
    preventScrollOnSwipe: true,
  });

  const handleButtonClick = async () => {
    if (!canAdvance) return;

    if (!isLast) {
      nextSlide();
      return;
    }

    if (submitting) return;
    setSubmitting(true);

    try {
      await userAPI.completeOnboarding(user.id);
      // Первый вход: после онбординга на LuckySpin запускаются подсказки туториала
      dispatch(startTutorial(user.id));
      setUser({ ...user, onboarding: true });
      navigate('/');
    } catch (error) {
      console.error('❌ Failed to complete onboarding:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });
      setSubmitting(false);
    }
  };

  return (
    <div className="onboarding-container">
      <div className="ob-glow" aria-hidden="true" />

      <header className="ob-header">
        <div className="brand">
          <img src="/logo.png" alt="Logo" className="brand-icon" />
          <div className="brand-text-wrapper">
            <img src="/datum.png" alt="DATUM" className="brand-text datum" />
            <span className="empire">EMPIRE</span>
          </div>
        </div>
        <span className="ob-header-title">{t(ONBOARDING_BUTTONS.header)}</span>
      </header>

      <section className="ob-card" {...swipeHandlers}>
        <div className="ob-media">
          <div
            className="ob-track"
            style={{ transform: `translate3d(-${currentIndex * 100}%, 0, 0)` }}
          >
            {slides.map((s, index) => (
              <div key={s.id} className="ob-slide" aria-hidden={index !== currentIndex}>
                <SlideMedia
                  slide={s}
                  active={index === currentIndex}
                  loaded={loadedImages[s.image]}
                  onLoad={() => markLoaded(s.image)}
                />
              </div>
            ))}
          </div>

          <button
            type="button"
            className="ob-arrow ob-arrow--left"
            onClick={prevSlide}
            disabled={currentIndex === 0}
            aria-label={translate('onboarding.aria.previous', 'Previous slide')}
          >
            <img src="/arrow-left.svg" alt="" />
          </button>
          <button
            type="button"
            className="ob-arrow ob-arrow--right"
            onClick={nextSlide}
            disabled={isLast || !canAdvance}
            aria-label={translate('onboarding.aria.next', 'Next slide')}
          >
            <img src="/arrow-right.svg" alt="" />
          </button>
        </div>

        {/* key перезапускает анимацию появления текста при смене слайда */}
        <div className="ob-text" key={currentIndex}>
          <h2 className="ob-title">{t(slide.title)}</h2>
          <p className={`ob-desc ${features ? 'ob-desc--compact' : ''}`}>{t(slide.description)}</p>

          {features && (
            <ul className="ob-features">
              {features.map((f, i) => (
                <li key={f.text.key} className={`ob-feature ${i === 0 ? 'is-accent' : ''}`}>
                  <OnboardingIcon icon={f.icon} color={f.color} round={f.round} />
                  <span>{t(f.text)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="ob-footer">
          <div className="ob-dots" role="tablist">
            {slides.map((s, i) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={i === currentIndex}
                aria-label={translate('onboarding.aria.slide', 'Slide {{n}}', { n: i + 1 })}
                className={`ob-dot ${i === currentIndex ? 'is-active' : ''}`}
                disabled={i > readIndex + 1}
                onClick={() => goTo(i)}
              />
            ))}
          </div>

          <button
            type="button"
            className={`ob-button ${canAdvance ? '' : 'is-waiting'}`}
            onClick={handleButtonClick}
            disabled={submitting || !canAdvance}
          >
            {/* key перезапускает полоску ожидания на каждом новом слайде */}
            {!canAdvance && (
              <span
                key={currentIndex}
                className="ob-button-progress"
                style={{ animationDuration: `${READ_DELAY_MS}ms` }}
                aria-hidden="true"
              />
            )}
            <span className="ob-button-label">
              {submitting ? t(ONBOARDING_BUTTONS.loading) : t(isLast ? ONBOARDING_BUTTONS.start : ONBOARDING_BUTTONS.next)}
            </span>
          </button>

          {slide.footnote && <p className="ob-footnote">{t(slide.footnote)}</p>}
        </footer>
      </section>
    </div>
  );
}
